import { HttpService, HttpServiceResponse } from "../shared";
import {
  DrilldownData,
  Durations,
  ReportData,
  GetReportArgs,
} from "./reports.types";
import {
  CachedResponse,
  ResponseCache,
  ResponseValidator,
} from "../shared/response.cache";

const isResponseStale =
  (reportingTimeout: number): ResponseValidator<ReportData> =>
  (response) => {
    const { createdAt } = response;
    const timestamp = Date.parse(createdAt);
    const currentTimestamp = Date.now();
    const differenceInSeconds = Math.floor(
      (currentTimestamp - timestamp) / 1000
    );
    const timeout = reportingTimeout * 60;

    return differenceInSeconds >= timeout;
  };

export const REPORTS_CACHE_NAME = "reports";

export class ReportsProvider {
  private reportsCache: ResponseCache<ReportData>;

  constructor(
    private http: HttpService,
    private baseUrl: string,
    private reportingTimeout: number
  ) {
    this.reportsCache = new ResponseCache(
      REPORTS_CACHE_NAME,
      isResponseStale(reportingTimeout)
    );
  }

  private get url(): string {
    return `${this.baseUrl}/ssm/series`;
  }

  private createEndpoint(
    endpoint: string,
    subscriberId: string,
    duration: Durations,
    profileName?: string,
    limit?: number,
    adjustGranularity: boolean = true
  ): string {
    const profile = profileName ? `&group_name=${profileName}` : "";
    const currentLimit = limit ? `&limit=${limit}` : "";
    const withAdjustGranularity = adjustGranularity
      ? "&adjust_granularity=true"
      : "";

    return `${endpoint}?view=${subscriberId}&duration=${duration}${profile}${currentLimit}${withAdjustGranularity}`;
  }

  public async getDrilldowns(
    ...args: GetReportArgs
  ): Promise<HttpServiceResponse<DrilldownData>> {
    const endpoint = this.createEndpoint(...args);

    return this.http.get<DrilldownData>(`${this.url}/${endpoint}`);
  }

  public async getDrilldownsCsv(
    endpoint: string
  ): Promise<HttpServiceResponse<string>> {
    return this.http.get(`${endpoint}`);
  }

  public async get(
    ...args: GetReportArgs
  ): Promise<CachedResponse<ReportData>> {
    const cacheKey = this.createEndpoint(...args);

    const cachedResponse = this.reportsCache.getResponse(cacheKey);

    if (cachedResponse) {
      return Promise.resolve(cachedResponse);
    } else {
      try {
        const response = await this.http.get<ReportData>(
          `${this.url}/${cacheKey}`
        );

        const cachedResponse = this.reportsCache.saveResponse(
          cacheKey,
          response
        );

        return Promise.resolve(cachedResponse);
      } catch (e) {
        return Promise.reject(e);
      }
    }
  }
}
